// Angular
import { AfterViewInit, Component, ElementRef, Inject, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { LogService } from '@app/services/log.service';

// 3rdparty
import { fromEvent } from 'rxjs';
import { switchMap, takeUntil, pairwise } from 'rxjs/operators'
// Hostware

@Component({
  selector: 'app-signature-dialog',
  templateUrl: './signature-dialog.component.html',
  styleUrls: ['./signature-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SignatureDialogComponent implements AfterViewInit {

  @ViewChild('canvas', { static: true }) canvas: ElementRef<HTMLCanvasElement>;
  @ViewChild('canvasWrapper', { static: true }) canvasWrapper: ElementRef<HTMLDivElement>;

  private context: CanvasRenderingContext2D;
  private isDrawing = false;
  private lastPos: { x: number; y: number };
  // A görbe utolsó kirajzolt végpontja (két mintapont közti felezőpont)
  private lastMid: { x: number; y: number };

  constructor(
    private elementRef: ElementRef,
    public dialogRef: MatDialogRef<SignatureDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private logService: LogService
  ) { }

  ngAfterViewInit() {
    this.context = this.canvas.nativeElement.getContext('2d');
    this.setCanvasDimensions();

    // Prevent scrolling on touch devices
    const dialogContainer = this.elementRef.nativeElement.closest('.mat-mdc-dialog-container, .mat-dialog-container');
    if (dialogContainer) {
      dialogContainer.addEventListener('touchmove', (event: TouchEvent) => {
        if (this.isDrawing) {
          event.preventDefault();
        }
      }, { passive: false });
    }
  }

  private setCanvasDimensions() {
    if (this.canvas && this.canvas.nativeElement && this.canvasWrapper && this.canvasWrapper.nativeElement) {
      const canvasEl = this.canvas.nativeElement;
      const wrapperEl = this.canvasWrapper.nativeElement;
      
      // Use clientWidth/clientHeight for integer-based dimensions of the container
      const displayWidth = wrapperEl.clientWidth;
      const displayHeight = wrapperEl.clientHeight;
      
      const scale = window.devicePixelRatio || 1;

      // Set the internal drawing buffer size for high-resolution rendering
      canvasEl.width = displayWidth * scale;
      canvasEl.height = displayHeight * scale;

      // Set the CSS size to make the canvas visually fit the container
      canvasEl.style.width = `${displayWidth}px`;
      canvasEl.style.height = `${displayHeight}px`;

      // Scale the drawing context to handle the high-DPI resolution.
      // All subsequent drawing operations will be scaled automatically.
      this.context.scale(scale, scale);

      // Set drawing properties
      this.context.lineWidth = 2;
      this.context.lineCap = 'round';
      this.context.lineJoin = 'round';
      this.context.strokeStyle = 'black';
    }
  }

 
  private getPointerPositionOnCanvas(event: PointerEvent): { x: number; y: number } {
    const rect = this.canvas.nativeElement.getBoundingClientRect();
    // The context is already scaled, so we just need the position relative to the canvas element.
    // The browser correctly maps the clientX/Y to the scaled canvas.
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  onPointerDown(event: PointerEvent) {
    // A vonal a canvason kívülre húzva sem szakad meg
    this.canvas.nativeElement.setPointerCapture?.(event.pointerId);
    this.isDrawing = true;
    this.lastPos = this.getPointerPositionOnCanvas(event);
    this.lastMid = this.lastPos;
  }

  onPointerMove(event: PointerEvent) {
    if (!this.isDrawing) {
      return;
    }

    // Androidon a böngésző képkockánként csak egy pointermove-ot küld, a köztes érintési pontok
    // a coalesced eseményekben vannak. Ezek nélkül a vonal hosszú egyenes szakaszokból állna.
    const coalesced = typeof event.getCoalescedEvents === 'function' ? event.getCoalescedEvents() : [];
    const events = coalesced.length > 0 ? coalesced : [event];

    this.context.beginPath();
    this.context.moveTo(this.lastMid.x, this.lastMid.y);
    for (const e of events) {
      const pos = this.getPointerPositionOnCanvas(e);
      const mid = {
        x: (this.lastPos.x + pos.x) / 2,
        y: (this.lastPos.y + pos.y) / 2
      };
      // Felezőponttól felezőpontig rajzolunk, a mintapont a kontrollpont: így a szakaszok
      // érintőlegesen csatlakoznak, nincs sarok.
      this.context.quadraticCurveTo(this.lastPos.x, this.lastPos.y, mid.x, mid.y);
      this.lastPos = pos;
      this.lastMid = mid;
    }
    this.context.stroke();
  }

  onPointerUp(event: PointerEvent) {
    if (this.isDrawing) {
      // Az utolsó felezőponttól a tényleges végpontig (egyetlen érintésnél egy pont)
      this.context.beginPath();
      this.context.moveTo(this.lastMid.x, this.lastMid.y);
      this.context.lineTo(this.lastPos.x, this.lastPos.y);
      this.context.stroke();
    }
    this.isDrawing = false;
  }

  onClear() {
        this.context.clearRect(0, 0, this.canvas.nativeElement.width, this.canvas.nativeElement.height);
  }

  onSave() {
    const dataUrl = this.canvas.nativeElement.toDataURL();
    this.dialogRef.close(dataUrl);
  }

  onCancel() {
    this.dialogRef.close();
  }
}
